import * as React from 'react'

import { Box, Href, Text } from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'

import { observer } from 'mobx-react'

import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'

import Hint from 'modules/contracts/components/FlowPage/FormWrapper/Hint'
import type Employee from 'modules/employees/store/models/Employee'
import {
  formatSalaryFromCents,
  isSalaryOnRange,
  salaryToCents,
} from 'modules/job_catalog/lib/salaryUtils'
import type JobLevelSalaryRange from 'modules/job_catalog/store/models/JobLevelSalaryRange'

interface Props {
  salaryAmountField: Field
  salaryFrequencyField: Field
  employee: Employee
  salaryRange: JobLevelSalaryRange
}

const getSalaryFrequency = (salaryFrequency: string | undefined) => {
  switch (salaryFrequency) {
    case 'yearly':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.yearly')
    case 'monthly':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.monthly')
    case 'weekly':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.weekly')
    case 'daily':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.daily')
    case 'hourly':
      return i18n.t('contract_versions.forms.gross_salary.factorial_tip.frequency.hourly')
  }
}

const SalaryRangeHint = ({
  salaryAmountField,
  salaryFrequencyField,
  employee,
  salaryRange,
}: Props) => {
  const { salary_frequency, min_range, max_range, currency, amount_type } = salaryRange.attributes
  const legalEntity = employeeLegalEntity(employee)
  const country = legalEntity.country

  const minRange = formatSalaryFromCents(min_range, country, currency)
  const maxRange = formatSalaryFromCents(max_range, country, currency)
  const translatedFrequency = getSalaryFrequency(salary_frequency)

  const salaryAlertMessage =
    amount_type === 'range'
      ? i18n.t('employee_form.wizard.role_step.salary_range_alert', {
          min_range: minRange,
          max_range: maxRange,
          salary_frequency: translatedFrequency,
        })
      : i18n.t('employee_form.wizard.role_step.salary_fixed_alert', {
          fixed_amount: minRange,
          salary_frequency: translatedFrequency,
        })

  const onRange = isSalaryOnRange(
    salaryToCents(salaryAmountField.value),
    salaryFrequencyField.value,
    salaryRange.attributes.salary_frequency,
    salaryRange.attributes.min_range,
    salaryRange.attributes.max_range,
    salaryRange.attributes.amount_type
  )
  const hintColor = onRange ? 'lime200' : 'champagne200'
  const hintTitle = onRange
    ? i18n.t('contract_versions.flows.fix.steps.role_level_salary.hints.on_range')
    : i18n.t('contract_versions.flows.fix.steps.role_level_salary.hints.range')

  return (
    <Hint color={hintColor}>
      {/* TODO obtain correct help center link */}
      <Href href={helpCenterUrl('payroll_incidences')} target="_blank">
        <Box>
          <Text.Normal size="100" weight="semibold" color="grey1000">
            {hintTitle}
          </Text.Normal>
          <Text.Normal size="100" weight="normal" color="grey1000">
            {salaryAlertMessage}
          </Text.Normal>
        </Box>
      </Href>
    </Hint>
  )
}

export default observer(SalaryRangeHint)
